import { useCallback, useState } from "react";
import Cropper from "react-easy-crop";
import { getCropConfig, getCroppedImageFile } from "utils/imageCropUtils";


export default function ImageCropperModal({ imageSrc, fileName, slotType, onConfirm, onCancel }) {
  const config = getCropConfig(slotType);

  const [crop,        setCrop]        = useState({ x: 0, y: 0 });
  const [zoom,        setZoom]        = useState(1);
  const [pixelCrop,   setPixelCrop]   = useState(null);
  const [processing,  setProcessing]  = useState(false);

  const onCropComplete = useCallback((_, pixels) => {
    setPixelCrop(pixels);
  }, []);

  const handleConfirm = async () => {
    if (!pixelCrop) return;
    setProcessing(true);
    try {
      const file = await getCroppedImageFile(imageSrc, pixelCrop, fileName, slotType);
      onConfirm(file);
    } catch (err) {
      console.error("Crop failed:", err);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        background: "rgba(0,0,0,0.7)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
      onClick={(e) => e.target === e.currentTarget && onCancel()}
    >
      <div style={{
        background: "#fff", borderRadius: 14, overflow: "hidden",
        width: "100%", maxWidth: 900,
        display: "flex", flexDirection: "column",
        boxShadow: "0 24px 64px rgba(0,0,0,0.3)",
      }}>
        {/* header */}
        <div style={{
          padding: "14px 18px",
          borderBottom: "0.5px solid rgba(0,0,0,0.10)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
          flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 14, fontWeight: 600, color: "#1a1a1a" }}>
              {config.label}
            </div>
            {config.hint && (
              <div style={{ fontSize: 11, color: "#a0a0a0", marginTop: 2 }}>
                {config.hint}
              </div>
            )}
          </div>
          <button
            onClick={onCancel}
            style={{
              width: 28, height: 28, border: "none", borderRadius: 7,
              background: "#f7f6f3", cursor: "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              color: "#6b6b6b",
            }}
          >
            <i className="ti ti-x" style={{ fontSize: 14 }} />
          </button>
        </div>

        {/* crop area */}
        <div style={{ position: "relative", width: "100%", height: 560, background: "#111" }}>
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={config.aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            style={{
              containerStyle: { borderRadius: 0 },
              cropAreaStyle:  {
                border: "2px solid #3b82f6",
                boxShadow: "0 0 0 9999px rgba(0,0,0,0.55)",
              },
            }}
          />
        </div>

        {/* zoom slider */}
        <div style={{
          padding: "12px 18px",
          borderTop: "0.5px solid rgba(0,0,0,0.08)",
          display: "flex", alignItems: "center", gap: 10,
          flexShrink: 0,
        }}>
          <i className="ti ti-zoom-out" style={{ fontSize: 14, color: "#a0a0a0" }} />
          <input
            type="range"
            min={1} max={3} step={0.05}
            value={zoom}
            onChange={(e) => setZoom(Number(e.target.value))}
            style={{ flex: 1, accentColor: "#3b82f6" }}
          />
          <i className="ti ti-zoom-in" style={{ fontSize: 14, color: "#a0a0a0" }} />
        </div>

        {/* footer */}
        <div style={{
          padding: "12px 18px 16px",
          display: "flex", justifyContent: "flex-end", gap: 8,
          flexShrink: 0,
        }}>
          <button
            onClick={onCancel}
            style={{
              padding: "7px 16px", border: "0.5px solid rgba(0,0,0,0.12)",
              borderRadius: 7, background: "#fff", fontSize: 13,
              fontWeight: 500, cursor: "pointer", fontFamily: "inherit",
              color: "#1a1a1a",
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!pixelCrop || processing}
            style={{
              padding: "7px 18px", border: "none", borderRadius: 7,
              background: pixelCrop && !processing ? "#1a1a1a" : "#e5e7eb",
              color: pixelCrop && !processing ? "#fff" : "#9ca3af",
              fontSize: 13, fontWeight: 500,
              cursor: pixelCrop && !processing ? "pointer" : "default",
              fontFamily: "inherit",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            {processing && (
              <i className="ti ti-loader-2" style={{ fontSize: 13, animation: "spin 1s linear infinite" }} />
            )}
            {processing ? "Processing…" : "Apply crop"}
          </button>
        </div>
      </div>
    </div>
  );
}